import { AcknowledgementGroupConfig, AcknowledgementPointConfig } from '@/types/subscriptionAgreement'

export const HIPAA_ACKNOWLEDGEMENT_GROUPS: AcknowledgementGroupConfig[] = [
  { id: 'A', label: 'Privacy & access to patient information' },
  { id: 'B', label: 'Securing devices and data' },
  { id: 'C', label: 'Platform conduct and AI tools' },
  { id: 'D', label: 'Incidents and accountability' },
]

export const HIPAA_ACKNOWLEDGEMENT_POINTS: AcknowledgementPointConfig[] = [
  {
    id: 'point-1',
    group: 'A',
    number: 1,
    title: 'Confidentiality and lawful use of PHI',
    body: [
      'I will keep patient information confidential and use or disclose PHI only for lawful purposes permitted by HIPAA and PillarThera policy — treatment, payment, healthcare operations, care coordination, scheduling, documentation, billing, and compliance. This duty continues after my access ends.',
    ],
    ref: 'Agreement §3.1, §3.4, §3.6',
  },
  {
    id: 'point-2',
    group: 'A',
    number: 2,
    title: 'Minimum necessary and business-need access',
    body: [
      'I will access only the minimum PHI needed to do my job. I will not look at patient records out of curiosity, personal interest, personal relationship, or convenience — even if I have technical access to them.',
    ],
    ref: 'Agreement §3.2, §3.3',
  },
  {
    id: 'point-3',
    group: 'A',
    number: 3,
    title: 'My own credentials, and MFA stays on',
    body: [
      'I will use only my own login, keep my password and authentication codes private, never share credentials or let anyone use my account, and keep Multi-Factor Authentication enabled. I will report a suspected compromise to PillarThera immediately.',
    ],
    ref: 'Agreement §4.1–4.3, §7.1',
  },
  {
    id: 'point-4',
    group: 'B',
    number: 4,
    title: 'Secure devices, networks, and remote work',
    body: [
      'I will use password- or biometric-protected devices that lock automatically and run supported, updated software. I will connect over secure networks, avoid unsecured public Wi-Fi, and take the same care in patient homes and public places as I would in the office — including preventing others from seeing my screen.',
    ],
    ref: 'Agreement §5.1, §5.2, §5.4',
  },
  {
    id: 'point-5',
    group: 'B',
    number: 5,
    title: 'No unauthorized export, printing, or recording',
    body: [
      'I will not download, export, copy, print, screenshot, photograph, or screen-record PHI outside PillarThera unless it is necessary for a legitimate purpose, authorized, and properly secured. Anything I do take out I will safeguard, keep no longer than needed, and destroy securely.',
    ],
    ref: 'Agreement §6.3, §6.4',
  },
  {
    id: 'point-6',
    group: 'B',
    number: 6,
    title: 'Approved communication channels only',
    body: [
      "I will not send PHI through personal email, consumer messaging apps, social media, or other unsecured channels. I will use PillarThera's secure messaging or another method my agency has approved.",
    ],
    ref: 'Agreement §6.5',
  },
  {
    id: 'point-7',
    group: 'C',
    number: 7,
    title: 'Platform integrity',
    body: [
      'I will not disable, bypass, or test security controls, audit logging, or access controls; reverse engineer or copy the Platform; or use bots, scrapers, or automated tools to extract data outside approved APIs. Security testing requires PillarThera’s prior written authorization. Reporting a vulnerability in good faith is always welcome.',
    ],
    ref: 'Agreement §7.2, §7.5',
  },
  {
    id: 'point-8',
    group: 'C',
    number: 8,
    title: 'No falsified records or fraudulent claims',
    body: [
      'I will not falsify visit records, Electronic Visit Verification (EVV) data, clinical notes, billing records, or claims; misrepresent credentials or patient information; or use the Platform to obtain payment improperly. I understand suspected fraud may be reported to government authorities.',
    ],
    ref: 'Agreement §7.3',
  },
  {
    id: 'point-9',
    group: 'C',
    number: 9,
    title: 'AI is assistive — a human reviews before it counts',
    body: [
      'I understand PillarVoice, PillarMatch, PillarClaim, and PillarRoute produce assistive output that can contain errors. A qualified person will review AI-generated content before clinical documentation is finalized, claims are submitted, invoices are issued, patient communications are sent, or schedules affecting care are approved. I will not represent AI output as verified unless it actually was.',
    ],
    ref: 'Agreement §7.4, §11.2, §11.3, §11.5',
  },
  {
    id: 'point-10',
    group: 'D',
    number: 10,
    title: 'Report incidents within 24 hours',
    body: [
      'I will report any suspected or confirmed loss, theft, unauthorized access, or improper disclosure of PHI — including lost devices, phishing, malware, and misdirected messages — to PillarThera within 24 hours of becoming aware. I will not wait until an internal investigation is finished to report.',
    ],
    ref: 'Agreement §8.1, §8.2',
  },
  {
    id: 'point-11',
    group: 'D',
    number: 11,
    title: 'Workforce training, provisioning, and access reviews',
    body: [
      'My agency will train each user on HIPAA and these obligations before granting access, grant access on a least-privilege basis, remove access promptly when someone leaves or changes roles, and review accounts periodically to remove dormant, duplicate, or over-privileged access. My agency remains responsible for its workforce’s conduct.',
    ],
    ref: 'Agreement §4.4–4.6, §9.1–9.4',
  },
  {
    id: 'point-12',
    group: 'D',
    number: 12,
    title: 'Monitoring, audit logs, and consequences',
    body: [
      'I understand PillarThera logs Platform activity — logins, record access, changes, and electronic signatures — and may monitor for security threats and misuse. My agency will cooperate with reasonable security investigations. Violations may lead to retraining, restricted features, suspended or terminated access, contractual action, and legal or regulatory penalties.',
    ],
    ref: 'Agreement §10.1–10.3, §12.2, §12.3',
  },
]
